/**
 * ArticleDetailView — full bilingual detail for a single PubMed
 * article. Renders, top-to-bottom:
 *
 *   ┌─ view title (文章详情 · PMID <id>)
 *   ├─ article meta (PMID + journal + pubdate + authors +
 *   │                 affiliations + DOI / PubMed links + diseases)
 *   ├─ bilingual title (EN serif + ZH muted)
 *   ├─ bilingual abstract (full text — no clamp on detail view)
 *   └─ error / 404 / loading rows
 *
 * 404 behavior: when `fetchArticle` returns null we render a 404
 * placeholder rather than throwing — keeps the route resilient when
 * the user clicks a stale link.
 *
 * Convention #6: titles + abstracts ALWAYS render EN + ZH side-by-
 * side. When the worker hasn't translated yet the right column shows
 * the fallback string `（暂无中文翻译）`.
 */

import { el, setChildren } from "../dom";
import { ApiError, fetchArticle } from "../api";
import { setCacheMapEntry, getStore } from "../store";
import { articleMeta } from "../components/articleMeta";
import { navigate } from "../router";
import type { ArticleDetail } from "../types";

export function renderArticleView(pmid: string): HTMLElement {
  const root = el("section", { class: "view view-article", "data-view": "article" }, [
    el("div", { class: "view-title" }, [
      el("h1", null, `文章详情 · PMID ${pmid}`),
      el("p", { class: "view-title-sub" }, "正在加载…"),
    ]),
    el("div", { class: "status-row" }, [
      el("div", { class: "spinner", "aria-hidden": "true" }),
      el("div", { class: "status-row-title" }, "正在加载文章…"),
    ]),
  ]);

  void load();
  return root;

  async function load(): Promise<void> {
    const cached = getStore().cache.article?.[pmid];
    if (cached) {
      paint(cached);
      return;
    }

    try {
      const article = await fetchArticle(pmid);
      setCacheMapEntry("article", pmid, article);
      paint(article);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        paintNotFound();
        return;
      }
      const error = err instanceof ApiError ? err : null;
      setChildren(root, [
        el("div", { class: "view-title" }, [
          el("h1", null, `文章详情 · PMID ${pmid}`),
          el("p", { class: "view-title-sub" }, "无法加载"),
        ]),
        el("div", { class: "status-row is-error" }, [
          el("div", { class: "status-row-title" }, "无法加载"),
          el(
            "div",
            { class: "status-row-detail" },
            error ? `${error.status} · ${error.message}` : "网络错误，请稍后重试。",
          ),
        ]),
      ]);
    }
  }

  function paint(article: ArticleDetail): void {
    const title = el("div", { class: "view-title" }, [
      el("h1", null, `文章详情 · PMID ${article.pmid}`),
      el("p", { class: "view-title-sub" }, article.journal_abbr ?? article.journal),
    ]);

    const meta = articleMeta({ article });

    const titleBlock = el("section", { class: "article-title-block" }, [
      el("h2", { class: "article-title-en" }, article.title || "（无标题）"),
      el(
        "h3",
        { class: "article-title-zh" },
        article.title_zh ?? "（暂无中文翻译）",
      ),
    ]);

    const abstractBlock = el("section", { class: "article-abstract-block" }, [
      el("h2", { class: "article-abstract-heading" }, "摘要"),
      el("p", { class: "article-abstract-en" }, article.abstract || "（无英文摘要）"),
      el(
        "p",
        { class: "article-abstract-zh" },
        article.abstract_zh ?? "（暂无中文翻译）",
      ),
    ]);

    const body = el("div", { class: "main-pane view-body" }, [
      meta,
      titleBlock,
      abstractBlock,
    ]);

    setChildren(root, [title, body]);
  }

  function paintNotFound(): void {
    setChildren(root, [
      el("div", { class: "view-title" }, [
        el("h1", null, "404"),
        el("p", { class: "view-title-sub" }, `PMID ${pmid} 不存在`),
      ]),
      el("div", { class: "placeholder-view" }, [
        el("p", null, "未找到该 PMID 对应的文章记录。"),
        el("p", null, [
          el("a", {
            href: "#/daily",
            onclick: (ev: Event) => {
              const me = ev as MouseEvent;
              if (me.metaKey || me.ctrlKey || me.button !== 0) return;
              ev.preventDefault();
              navigate("/daily");
            },
          }, "返回每日文献动态"),
        ]),
      ]),
    ]);
  }
}